(function () {
  "use strict";

  const SELECTORS = {
    container: "[data-predictive-search-container]",
    form: "[data-predictive-search-form]",
    input: "[data-predictive-search-input]",
    results: "[data-predictive-search-results]",
    loading: "[data-predictive-search-loading]",
    emptyState: "[data-predictive-search-empty-state]",
  };

  const MOBILE_SELECTORS = {
    drawer: "[data-mobile-search-drawer]",
    form: "[data-mobile-search-form]",
    input: "[data-mobile-search-input]",
    results: "[data-mobile-search-results]",
    loading: "[data-mobile-search-loading]",
    emptyState: "[data-mobile-search-empty-state]",
  };

  const DEBOUNCE_MS = 250;
  const MIN_QUERY_LENGTH = 2;
  const PRODUCTS_LIMIT = 4;
  const SUGGESTIONS_LIMIT = 4;
  const COLLECTIONS_LIMIT = 4;

  function escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  function sanitizeHighlight(str) {
    const tokens = str.split(/(<em>|<\/em>)/g);
    return tokens
      .map((token) => {
        if (token === "<em>" || token === "</em>") return token;
        return escapeHtml(token);
      })
      .join("");
  }

  function init() {
    const configEl = document.querySelector("[data-algolia-config]");
    if (!configEl) return;

    const appId = configEl.getAttribute("data-algolia-app-id");
    const apiKey = configEl.getAttribute("data-algolia-api-key");
    const indexPrefix =
      configEl.getAttribute("data-algolia-index-prefix") || "shopify_";
    const moneyFormat =
      configEl.getAttribute("data-money-format") || "{{amount}} \u20AC";
    const locale = configEl.getAttribute("data-locale") || "de";
    const rootUrl = configEl.getAttribute("data-root-url") || "/";
    const searchUrl = configEl.getAttribute("data-search-url") || "/search";
    const excludedCollections = (
      configEl.getAttribute("data-algolia-excluded-collections") || ""
    )
      .split(",")
      .map((handle) => handle.trim())
      .filter(Boolean);
    const excludedCollectionHandles = new Set(excludedCollections);

    const translationsScript = document.querySelector(
      "[data-algolia-translations-json]",
    );
    const translations = translationsScript
      ? JSON.parse(translationsScript.textContent)
      : {};

    if (!appId || !apiKey) return;

    const arrowTemplate = document.querySelector("[data-icon-arrow-template]");
    const fireTemplate = document.querySelector("[data-icon-fire-template]");
    const leafTemplate = document.querySelector("[data-icon-leaf-template]");
    const seedlingTemplate = document.querySelector(
      "[data-icon-seedling-template]",
    );
    const trophyTemplate = document.querySelector(
      "[data-icon-trophy-template]",
    );
    const arrowSvg = arrowTemplate ? arrowTemplate.innerHTML.trim() : "";
    const fireSvg = fireTemplate ? fireTemplate.innerHTML.trim() : "";
    const leafSvg = leafTemplate ? leafTemplate.innerHTML.trim() : "";
    const seedlingSvg = seedlingTemplate
      ? seedlingTemplate.innerHTML.trim()
      : "";
    const trophySvg = trophyTemplate ? trophyTemplate.innerHTML.trim() : "";
    const circleXTemplate = document.querySelector(
      "[data-icon-circle-x-template]",
    );
    const circleXSvg = circleXTemplate ? circleXTemplate.innerHTML.trim() : "";

    const productsIndex =
      locale === "de"
        ? `${indexPrefix}products`
        : `${indexPrefix}products_deutschland_en`;
    const suggestionsIndex = `${indexPrefix}products_query_suggestions`;
    const collectionsIndex = `${indexPrefix}collections`;

    const form = document.querySelector(SELECTORS.form);
    if (!form) return;

    const input = form.querySelector(SELECTORS.input);
    const results = document.querySelector(SELECTORS.results);
    if (!input || !results) return;

    let debounceTimer = null;
    let currentController = null;
    let lastFetchedQuery = "";
    const emptyState = results.querySelector(SELECTORS.emptyState);

    function open() {
      results.classList.remove("hidden");
      input.setAttribute("aria-expanded", "true");
    }

    function close() {
      results.classList.add("hidden");
      input.setAttribute("aria-expanded", "false");
      hideEmptyState();
    }

    function showEmptyState() {
      if (emptyState) {
        const existing = results.querySelector("#predictive-search-results");
        if (existing) existing.remove();
        const loading = results.querySelector(SELECTORS.loading);
        if (loading) loading.classList.add("hidden");
        emptyState.classList.remove("hidden");
        open();
      }
    }

    function hideEmptyState() {
      if (emptyState) {
        emptyState.classList.add("hidden");
      }
    }

    function showLoading() {
      const loading = results.querySelector(SELECTORS.loading);
      const existing = results.querySelector("#predictive-search-results");
      if (loading) loading.classList.remove("hidden");
      if (existing) existing.remove();
      hideEmptyState();
    }

    function algoliaMultiQuery(queries, signal) {
      const url = `https://${appId}-dsn.algolia.net/1/indexes/*/queries`;

      return fetch(url, {
        method: "POST",
        headers: {
          "X-Algolia-Application-Id": appId,
          "X-Algolia-API-Key": apiKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ requests: queries }),
        signal,
      }).then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      });
    }

    function formatMoney(price) {
      const num = parseFloat(price);
      const amountDot = num.toFixed(2);
      const amountComma = amountDot.replace(".", ",");
      return moneyFormat
        .replace("{{amount_with_comma_separator}}", amountComma)
        .replace(
          "{{amount_no_decimals_with_comma_separator}}",
          Math.round(num).toString(),
        )
        .replace("{{amount}}", amountDot)
        .replace("{{amount_no_decimals}}", Math.round(num).toString());
    }

    function buildStoreUrl(path) {
      const normalizedRoot =
        rootUrl && rootUrl !== "/" ? rootUrl.replace(/\/$/, "") : "";
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return `${normalizedRoot}${normalizedPath}`;
    }

    function buildProductUrl(hit) {
      const url = buildStoreUrl(`/products/${hit.handle}`);
      return hit.objectID
        ? `${url}?variant=${encodeURIComponent(hit.objectID)}`
        : url;
    }

    function getTagsLower(hit) {
      const tags = Array.isArray(hit.tags) ? hit.tags : [];
      return tags.map((tag) => String(tag).toLowerCase().trim());
    }

    function getBadgeTypeFromHit(hit) {
      const tagsLower = getTagsLower(hit);
      const inventoryQuantity = Number(hit.inventory_quantity);
      const hasInventoryQuantity = Number.isFinite(inventoryQuantity);
      const isSoldOut =
        hit.inventory_available === false ||
        (hasInventoryQuantity && inventoryQuantity <= 0);
      const isLowStock =
        !isSoldOut &&
        hasInventoryQuantity &&
        inventoryQuantity > 0 &&
        inventoryQuantity <= 20;

      return window.PeakBadges.resolveBadgeType({
        isSoldOut,
        isLowStock,
        isBestSeller: tagsLower.includes("bestseller"),
        hasHoheNachfrage:
          tagsLower.includes("hohe-nachfrage") ||
          tagsLower.includes("hohe nachfrage"),
        hasVegan: tagsLower.includes("vegan"),
        hasBio: tagsLower.includes("bio"),
      });
    }

    function buildBadgeHtml(badgeType, extraClasses) {
      if (badgeType === "sold-out") {
        return (
          `<div class="${extraClasses} flex items-center gap-1 px-2 py-1 rounded-full" style="background: #6b7280">` +
          `<span class="text-white">${circleXSvg}</span><span class="caption text-white">${translations.sold_out || "Ausverkauft"}</span>` +
          `</div>`
        );
      }

      if (badgeType === "low-stock") {
        return (
          `<div class="${extraClasses} flex items-center gap-1 px-2 py-1 rounded-full" style="background: #dc2626">` +
          `<span style="color: #fecaca">${fireSvg}</span><span class="caption text-white">${translations.badge_low_stock || "Bald ausverkauft"}</span>` +
          `</div>`
        );
      }

      if (badgeType === "best-seller") {
        return (
          `<div class="${extraClasses} bg-accent flex items-center gap-1 px-2 py-1 rounded-full">` +
          `<span class="text-primary">${trophySvg}</span><span class="caption text-primary">${translations.badge_best_seller || "Bestseller"}</span>` +
          `</div>`
        );
      }

      if (badgeType === "hohe-nachfrage") {
        return (
          `<div class="${extraClasses} bg-gradient-dark flex items-center gap-1 px-2 py-1 rounded-full">` +
          `${fireSvg}<span class="caption text-white">${translations.badge_high_demand || "Hohe Nachfrage"}</span>` +
          `</div>`
        );
      }

      if (badgeType === "vegan") {
        return (
          `<div class="${extraClasses} flex items-center gap-1 px-2 py-1 rounded-full" style="background: #059669">` +
          `<span style="color: #a7f3d0">${seedlingSvg}</span><span class="caption text-white">${translations.badge_vegan}</span>` +
          `</div>`
        );
      }

      if (badgeType === "bio") {
        return (
          `<div class="${extraClasses} flex items-center gap-1 px-2 py-1 rounded-full" style="background: #15803d">` +
          `<span style="color: #bbf7d0">${leafSvg}</span><span class="caption text-white">${translations.badge_bio}</span>` +
          `</div>`
        );
      }

      return "";
    }

    function buildProductCard(hit) {
      const url = buildProductUrl(hit);
      const image = hit.image || "";
      const title = hit._highlightResult?.title?.value
        ? sanitizeHighlight(hit._highlightResult.title.value)
        : escapeHtml(hit.title || "");
      const price = hit.price || 0;
      const compareAtPrice = hit.compare_at_price || 0;
      const isOnSale = compareAtPrice > price && compareAtPrice > 0;
      const variants =
        hit.variants_count || (hit.variants ? hit.variants.length : 0);
      const optionNames = hit.options || {};
      let firstVariant = "";
      const optionKeys = Object.keys(optionNames);
      if (optionKeys.length > 0) {
        const val = optionNames[optionKeys[0]];
        if (typeof val === "string" && val) {
          firstVariant = val;
        } else if (Array.isArray(val) && val.length > 0) {
          firstVariant = val[0];
        }
      }
      const badgeType = getBadgeTypeFromHit(hit);
      const badgeHtml = buildBadgeHtml(badgeType, "absolute top-2 left-2");

      let variantPillsHtml = "";
      if (variants > 1) {
        const remaining = variants - 1;
        const moreLabel = `+${remaining}`;
        variantPillsHtml =
          `<div class="overflow-hidden w-full">` +
          `<div class="flex items-center">${
            firstVariant
              ? `<span class="shrink-0 mr-[5px] caption text-subdued px-2 py-[3px] rounded-full bg-white whitespace-nowrap">${escapeHtml(
                  firstVariant,
                )}</span>`
              : ""
          }<span class="shrink-0 mr-[5px] caption text-subdued px-2 py-[3px] rounded-full bg-white whitespace-nowrap">${
            moreLabel
          }</span>` +
          `</div></div>`;
      }

      let priceHtml = `<p class="body-small font-semibold ${
        isOnSale ? "text-discount" : ""
      }">${formatMoney(price)}</p>`;
      if (isOnSale) {
        priceHtml += `<p class="body-small font-light line-through">${formatMoney(
          compareAtPrice,
        )}</p>`;
      }

      const imgSrc = image ? `${image.replace(/\?.*$/, "")}?width=600` : "";
      const imgHtml = imgSrc
        ? `<img src="${imgSrc}" loading="lazy" class="w-full h-[90%] object-contain" alt="">`
        : '<div class="w-full h-full bg-bg-gray"></div>';

      return (
        `<article class="product-card relative flex flex-col h-full group overflow-clip bg-bg-gray rounded-md gap-[13px] p-[8px] lg:hover:bg-smoky-black/10 transition-colors">` +
        `<div class="bg-white relative aspect-square overflow-hidden rounded-md">` +
        `<a href="${url}" class="w-full h-full flex items-center justify-center" tabindex="-1" aria-hidden="true">${
          imgHtml
        }</a>${badgeHtml}</div>` +
        `<div class="flex flex-col items-start gap-[8px] w-full flex-1">` +
        `<div class="flex flex-col items-start gap-[5px] w-full">` +
        `<h3 class="product-card__title body-small font-medium">` +
        `<a href="${url}" class="after:absolute after:inset-0">${title}</a>` +
        `</h3>` +
        `</div>${
          variantPillsHtml
        }<div class="flex gap-2 items-baseline flex-wrap mt-auto pl-1 pb-1">${priceHtml}</div>` +
        `</div>` +
        `</article>`
      );
    }

    function buildMobileProductCard(hit) {
      const url = buildProductUrl(hit);
      const image = hit.image || "";
      const title = hit._highlightResult?.title?.value
        ? sanitizeHighlight(hit._highlightResult.title.value)
        : escapeHtml(hit.title || "");
      const price = hit.price || 0;
      const compareAtPrice = hit.compare_at_price || 0;
      const isOnSale = compareAtPrice > price && compareAtPrice > 0;
      const variants =
        hit.variants_count || (hit.variants ? hit.variants.length : 0);
      const imgSrc = image ? `${image.replace(/\?.*$/, "")}?width=200` : "";
      const imgHtml = imgSrc
        ? `<img src="${imgSrc}" loading="lazy" class="w-full h-full object-contain" alt="">`
        : '<div class="w-full h-full bg-bg-gray"></div>';
      const badgeType = getBadgeTypeFromHit(hit);
      const badgeHtml = buildBadgeHtml(badgeType, "shrink-0 relative z-10");

      const optionNames = hit.options || {};
      let firstVariant = "";
      const optionKeys = Object.keys(optionNames);
      if (optionKeys.length > 0) {
        const val = optionNames[optionKeys[0]];
        if (typeof val === "string" && val) {
          firstVariant = val;
        } else if (Array.isArray(val) && val.length > 0) {
          firstVariant = val[0];
        }
      }

      let variantHtml = "";
      if (variants > 1) {
        const remaining = variants - 1;
        variantHtml =
          `<div class="overflow-hidden w-full">` +
          `<div class="flex items-center">${
            firstVariant
              ? `<span class="shrink-0 mr-[5px] caption text-subdued px-2 py-[3px] rounded-full bg-bg-gray whitespace-nowrap">${escapeHtml(firstVariant)}</span>`
              : ""
          }<span class="shrink-0 mr-[5px] caption text-subdued px-2 py-[3px] rounded-full bg-bg-gray whitespace-nowrap">+${remaining}</span>` +
          `</div></div>`;
      }

      let priceHtml = `<p class="body font-semibold ${
        isOnSale ? "text-discount" : ""
      }">${formatMoney(price)}</p>`;
      if (isOnSale) {
        priceHtml += `<p class="body font-light line-through">${formatMoney(
          compareAtPrice,
        )}</p>`;
      }

      return (
        `<article class="product-card relative flex items-center gap-8 rounded-md">` +
        `<div class="shrink-0 w-[80px] h-[80px] rounded-sm overflow-hidden relative">` +
        `<a href="${url}" class="w-full h-full flex items-center justify-center" tabindex="-1" aria-hidden="true">${imgHtml}</a></div>` +
        `<div class="flex flex-col items-start gap-[8px] flex-1 min-w-0">` +
        `<div class="flex flex-col items-start gap-[5px] w-full">` +
        `<div class="flex flex-wrap items-center gap-2 w-full">` +
        `<h3 class="product-card__title body font-medium text-primary">` +
        `<a href="${url}" class="after:absolute after:inset-0 [&_em]:font-bold [&_em]:not-italic">${title}</a></h3>` +
        `${badgeHtml}</div></div>` +
        `${variantHtml}` +
        `<div class="flex gap-2 items-baseline flex-wrap">${priceHtml}</div>` +
        `</div></article>`
      );
    }

    function buildSuggestionLink(suggestion, searchBaseUrl) {
      const query = suggestion.query || "";
      const highlighted = suggestion._highlightResult?.query?.value
        ? sanitizeHighlight(suggestion._highlightResult.query.value)
        : escapeHtml(query);
      const href = `${searchBaseUrl}?q=${encodeURIComponent(query)}&type=product`;

      const arrowIcon = arrowSvg
        ? `<span class="w-4 h-4 text-primary shrink-0 -rotate-45 inline-flex">${arrowSvg}</span>`
        : "";

      return (
        `<a href="${href}" class="flex items-center justify-between w-full group/link">` +
        `<span class="body-small text-primary group-hover/link:underline [&_em]:font-bold [&_em]:not-italic">${highlighted}</span>` +
        `${arrowIcon}</a>`
      );
    }

    function buildCollectionChip(hit) {
      const url = buildStoreUrl(`/collections/${hit.handle}`);
      const title = hit._highlightResult?.title?.value
        ? sanitizeHighlight(hit._highlightResult.title.value)
        : escapeHtml(hit.title || "");
      const imgSrc = hit.image
        ? `${hit.image.replace(/\?.*$/, "")}?width=96`
        : "";

      const imgHtml = imgSrc
        ? `<div class="w-6 h-6 rounded-full overflow-hidden shrink-0"><img src="${imgSrc}" loading="lazy" class="w-full h-full object-cover" alt=""></div>`
        : "";

      return (
        `<a href="${url}" class="flex items-center gap-2 px-3 py-[6px] rounded-full bg-bg-gray hover:bg-smoky-black/10 transition-colors group/chip">` +
        `${imgHtml}` +
        `<span class="caption font-medium text-primary group-hover/chip:underline [&_em]:font-bold [&_em]:not-italic">${title}</span>` +
        `</a>`
      );
    }

    function buildMobileSuggestionLink(suggestion, searchBaseUrl) {
      const query = suggestion.query || "";
      const highlighted = suggestion._highlightResult?.query?.value
        ? sanitizeHighlight(suggestion._highlightResult.query.value)
        : escapeHtml(query);
      const href = `${searchBaseUrl}?q=${encodeURIComponent(query)}&type=product`;

      return (
        `<a href="${href}" class="flex items-center justify-between w-full">` +
        `<span class="body text-primary [&_em]:font-bold [&_em]:not-italic">${highlighted}</span>` +
        `<svg class="w-4 h-4 text-primary shrink-0 -rotate-45" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
        `<path d="M4 12H20M20 12L14 6M20 12L14 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>` +
        `</svg></a>`
      );
    }

    function verifyAvailability(container) {
      if (!container) return;
      const cards = container.querySelectorAll("[data-product-handle]");
      const handles = [];
      const cardsByHandle = {};
      cards.forEach((el) => {
        const handle = el.getAttribute("data-product-handle");
        if (handle && !cardsByHandle[handle]) {
          handles.push(handle);
          cardsByHandle[handle] = el.closest("article");
        }
      });

      handles.forEach((handle) => {
        fetch(buildStoreUrl(`/products/${handle}.js`))
          .then((res) => {
            return res.json();
          })
          .then((product) => {
            if (product.available) return;
            const card = cardsByHandle[handle];
            if (!card) return;

            const existingBadge = card.querySelector("[data-badge]");
            if (existingBadge) existingBadge.remove();

            const badgeEl = card.querySelector(
              ".rounded-full[style*='background']",
            );
            if (!badgeEl) {
              const badgeTarget =
                card.querySelector(".aspect-square, .aspect-\\[4\\/5\\]") ||
                card;
              const badgeHtml =
                `<div data-badge class="absolute top-2 left-2 z-20 flex items-center gap-1 px-2 py-1 rounded-full" style="background: #6b7280">` +
                `<span class="text-white">${
                  circleXSvg
                }</span><span class="caption text-white">${
                  translations.sold_out || "Ausverkauft"
                }</span></div>`;
              badgeTarget.insertAdjacentHTML("afterbegin", badgeHtml);
            }
          })
          .catch(() => {});
      });
    }

    function renderResults(
      query,
      suggestionsHits,
      collectionHits,
      productHits,
    ) {
      const hasSuggestions = suggestionsHits.length > 0;
      const hasCollections = collectionHits.length > 0;
      const hasProducts = productHits.length > 0;

      if (!hasSuggestions && !hasCollections && !hasProducts) {
        const noResults =
          translations.no_results || "Keine Ergebnisse gefunden.";
        return (
          `<div id="predictive-search-results">` +
          `<div class="max-w-container-page mx-auto px-20 py-10">` +
          `<p class="body text-subdued">${noResults}</p>` +
          `</div></div>`
        );
      }

      let html = '<div id="predictive-search-results">';
      html +=
        '<div class="max-w-container-page mx-auto flex items-start px-20 py-10 gap-10">';

      if (hasSuggestions) {
        const suggestionsLabel = translations.suggestions || "Vorschl\u00e4ge";
        html += '<div class="w-[240px] shrink-0 flex flex-col gap-4">';
        html += `<p class="caption font-bold text-subdued uppercase tracking-wide">${
          suggestionsLabel
        }</p>`;
        suggestionsHits.forEach((hit) => {
          html += buildSuggestionLink(hit, searchUrl);
        });
        html += "</div>";
      }

      if (hasSuggestions && (hasCollections || hasProducts)) {
        html += '<div class="w-px self-stretch bg-gray-light shrink-0"></div>';
      }

      if (hasCollections || hasProducts) {
        html += '<div class="flex-1 min-w-0 flex flex-col gap-6">';

        if (hasCollections) {
          const collectionsLabel = translations.collections || "Kategorien";
          html += '<div class="flex flex-col gap-3">';
          html += `<p class="caption font-bold text-subdued uppercase tracking-wide">${collectionsLabel}</p>`;
          html += '<div class="flex flex-wrap gap-2">';
          collectionHits.forEach((hit) => {
            html += buildCollectionChip(hit);
          });
          html += "</div></div>";
        }

        if (hasProducts) {
          const productsLabel = translations.products || "Produkte";
          const viewAllLabel = translations.view_all || "Alle sehen";
          const viewAllHref = `${searchUrl}?q=${encodeURIComponent(query)}&type=product`;

          html += '<div class="flex flex-col gap-4">';
          html += '<div class="flex items-center justify-between">';
          html += `<p class="body font-bold text-primary">${productsLabel}</p>`;
          html += `<a href="${viewAllHref}" class="body font-bold text-primary underline">${
            viewAllLabel
          }</a>`;
          html += "</div>";
          html += '<div class="grid grid-cols-4 gap-5">';
          productHits.forEach((hit) => {
            html += buildProductCard(hit);
          });
          html += "</div></div>";
        }

        html += "</div>";
      }

      html += "</div></div>";
      return html;
    }

    function renderMobileResults(
      query,
      suggestionsHits,
      collectionHits,
      productHits,
    ) {
      const hasSuggestions = suggestionsHits.length > 0;
      const hasCollections = collectionHits.length > 0;
      const hasProducts = productHits.length > 0;

      if (!hasSuggestions && !hasCollections && !hasProducts) {
        const noResults =
          translations.no_results || "Keine Ergebnisse gefunden.";
        return (
          `<div data-mobile-results-content>` +
          `<p class="body text-subdued py-4">${noResults}</p>` +
          `</div>`
        );
      }

      let html = "<div data-mobile-results-content>";
      let needsDivider = false;

      if (hasSuggestions) {
        html += '<div class="flex flex-col gap-4">';
        suggestionsHits.forEach((hit) => {
          html += buildMobileSuggestionLink(hit, searchUrl);
        });
        html += "</div>";
        needsDivider = true;
      }

      if (hasCollections) {
        if (needsDivider)
          html += '<div class="w-full h-px bg-gray-light my-6"></div>';
        const collectionsLabel = translations.collections || "Kategorien";
        html += '<div class="flex flex-col gap-3">';
        html += `<p class="caption font-bold text-subdued uppercase tracking-wide">${collectionsLabel}</p>`;
        html += '<div class="flex flex-wrap gap-2">';
        collectionHits.forEach((hit) => {
          html += buildCollectionChip(hit);
        });
        html += "</div></div>";
        needsDivider = true;
      }

      if (hasProducts) {
        if (needsDivider)
          html += '<div class="w-full h-px bg-gray-light my-6"></div>';
        const productsLabel = translations.products || "Produkte";
        const viewAllLabel = translations.view_all || "Alle sehen";
        const viewAllHref = `${searchUrl}?q=${encodeURIComponent(query)}&type=product`;

        html += '<div class="flex flex-col gap-4">';
        html += '<div class="flex items-center justify-between">';
        html += `<p class="body font-bold text-primary">${productsLabel}</p>`;
        html += `<a href="${viewAllHref}" class="body font-bold text-primary underline">${viewAllLabel}</a>`;
        html += "</div>";
        html += '<div class="flex flex-col gap-4">';
        productHits.forEach((hit) => {
          html += buildMobileProductCard(hit);
        });
        html += "</div></div>";
      }

      html += "</div>";
      return html;
    }

    function fetchResults(query) {
      open();
      showLoading();

      if (currentController) {
        currentController.abort();
      }
      currentController = new AbortController();

      const queries = [
        {
          indexName: suggestionsIndex,
          params: `query=${encodeURIComponent(query)}&hitsPerPage=${SUGGESTIONS_LIMIT}`,
        },
        {
          indexName: productsIndex,
          params: `query=${encodeURIComponent(query)}&hitsPerPage=${
            PRODUCTS_LIMIT
          }&clickAnalytics=true`,
        },
        {
          indexName: collectionsIndex,
          params: `query=${encodeURIComponent(query)}&hitsPerPage=${COLLECTIONS_LIMIT}`,
        },
      ];

      algoliaMultiQuery(queries, currentController.signal)
        .then((data) => {
          const suggestionsHits = data.results[0] ? data.results[0].hits : [];
          const productHits = data.results[1] ? data.results[1].hits : [];
          const collectionHits = data.results[2]
            ? data.results[2].hits.filter(
                (hit) =>
                  !hit.handle || !excludedCollectionHandles.has(hit.handle),
              )
            : [];

          const loading = results.querySelector(SELECTORS.loading);
          if (loading) loading.classList.add("hidden");

          const existing = results.querySelector("#predictive-search-results");
          if (existing) existing.remove();

          const html = renderResults(
            query,
            suggestionsHits,
            collectionHits,
            productHits,
          );
          results.insertAdjacentHTML("beforeend", html);
          lastFetchedQuery = query;
          verifyAvailability(results);
          open();
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            close();
          }
        });
    }

    function onInput() {
      const query = input.value.trim();

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      if (query.length < MIN_QUERY_LENGTH) {
        if (query.length === 0) {
          showEmptyState();
        } else {
          close();
        }
        return;
      }

      hideEmptyState();
      debounceTimer = setTimeout(() => {
        fetchResults(query);
      }, DEBOUNCE_MS);
    }

    function onKeydown(event) {
      if (event.key === "Escape") {
        close();
        input.blur();
      }
    }

    function onClickOutside(event) {
      const container = document.querySelector(SELECTORS.container);
      if (
        container &&
        !container.contains(event.target) &&
        !results.contains(event.target)
      ) {
        close();
      }
    }

    input.addEventListener("input", onInput);
    input.addEventListener("keydown", onKeydown);
    input.addEventListener("focus", () => {
      const query = input.value.trim();
      if (query.length >= MIN_QUERY_LENGTH) {
        if (query === lastFetchedQuery) {
          open();
        } else {
          onInput();
        }
      } else if (query.length === 0) {
        showEmptyState();
      }
    });

    results.addEventListener("click", (event) => {
      const termLink = event.target.closest("[data-popular-search-term]");
      if (termLink) {
        event.preventDefault();
        const term = termLink.getAttribute("data-popular-search-term");
        input.value = term;
        input.focus();
        hideEmptyState();
        fetchResults(term);
      }
    });

    document.addEventListener("click", onClickOutside);

    /* ── Mobile Search ── */
    initMobileAlgoliaSearch(
      algoliaMultiQuery,
      suggestionsIndex,
      productsIndex,
      collectionsIndex,
      searchUrl,
      translations,
      formatMoney,
      buildMobileSuggestionLink,
      buildMobileProductCard,
      renderMobileResults,
      excludedCollections,
    );
  }

  function initMobileAlgoliaSearch(
    algoliaMultiQuery,
    suggestionsIndex,
    productsIndex,
    collectionsIndex,
    searchUrl,
    translations,
    formatMoney,
    buildMobileSuggestionLink,
    buildMobileProductCard,
    renderMobileResults,
    excludedCollections,
  ) {
    const toggle = document.querySelector("[data-mobile-search-toggle]");
    const drawer = document.querySelector(MOBILE_SELECTORS.drawer);
    if (!toggle || !drawer) return;

    const closeBtn = drawer.querySelector("[data-mobile-search-close]");
    const searchInput = drawer.querySelector(MOBILE_SELECTORS.input);
    const resultsContainer = drawer.querySelector(MOBILE_SELECTORS.results);
    const loadingEl = drawer.querySelector(MOBILE_SELECTORS.loading);
    const emptyState = drawer.querySelector(MOBILE_SELECTORS.emptyState);
    const excludedCollectionHandles = new Set(excludedCollections);

    let mobileDebounceTimer = null;
    let mobileController = null;

    function showMobileEmptyState() {
      if (emptyState) {
        const existing = resultsContainer.querySelector(
          "[data-mobile-results-content]",
        );
        if (existing) existing.remove();
        if (loadingEl) loadingEl.classList.add("hidden");
        emptyState.classList.remove("hidden");
      }
    }

    function hideMobileEmptyState() {
      if (emptyState) {
        emptyState.classList.add("hidden");
      }
    }

    function showMobileLoading() {
      if (loadingEl) loadingEl.classList.remove("hidden");
      const existing = resultsContainer.querySelector(
        "[data-mobile-results-content]",
      );
      if (existing) existing.remove();
      hideMobileEmptyState();
    }

    function hideMobileLoading() {
      if (loadingEl) loadingEl.classList.add("hidden");
    }

    function clearMobileResults() {
      const existing = resultsContainer.querySelector(
        "[data-mobile-results-content]",
      );
      if (existing) existing.remove();
      hideMobileLoading();
      hideMobileEmptyState();
    }

    function fetchMobileResults(query) {
      if (mobileController) {
        mobileController.abort();
      }
      mobileController = new AbortController();

      showMobileLoading();

      const queries = [
        {
          indexName: suggestionsIndex,
          params: `query=${encodeURIComponent(query)}&hitsPerPage=${SUGGESTIONS_LIMIT}`,
        },
        {
          indexName: productsIndex,
          params: `query=${encodeURIComponent(query)}&hitsPerPage=${PRODUCTS_LIMIT}&clickAnalytics=true`,
        },
        {
          indexName: collectionsIndex,
          params: `query=${encodeURIComponent(query)}&hitsPerPage=${COLLECTIONS_LIMIT}`,
        },
      ];

      algoliaMultiQuery(queries, mobileController.signal)
        .then((data) => {
          hideMobileLoading();

          const suggestionsHits = data.results[0] ? data.results[0].hits : [];
          const productHits = data.results[1] ? data.results[1].hits : [];
          const collectionHits = data.results[2]
            ? data.results[2].hits.filter(
                (hit) =>
                  !hit.handle || !excludedCollectionHandles.has(hit.handle),
              )
            : [];

          const existing = resultsContainer.querySelector(
            "[data-mobile-results-content]",
          );
          if (existing) existing.remove();

          const html = renderMobileResults(
            query,
            suggestionsHits,
            collectionHits,
            productHits,
          );
          resultsContainer.insertAdjacentHTML("beforeend", html);
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            hideMobileLoading();
          }
        });
    }

    function onMobileInput() {
      const query = searchInput.value.trim();

      if (mobileDebounceTimer) {
        clearTimeout(mobileDebounceTimer);
      }

      if (query.length < MIN_QUERY_LENGTH) {
        if (query.length === 0) {
          clearMobileResults();
          showMobileEmptyState();
        } else {
          clearMobileResults();
        }
        return;
      }

      hideMobileEmptyState();

      mobileDebounceTimer = setTimeout(() => {
        fetchMobileResults(query);
      }, DEBOUNCE_MS);
    }

    function openDrawer() {
      drawer.classList.remove("hidden");
      document.body.style.overflow = "hidden";
      if (searchInput) {
        setTimeout(() => {
          searchInput.focus();
          const query = searchInput.value.trim();
          if (query.length === 0) {
            showMobileEmptyState();
          }
        }, 100);
      }
    }

    function closeDrawer() {
      drawer.classList.add("hidden");
      document.body.style.overflow = "";
    }

    toggle.addEventListener("click", openDrawer);
    if (closeBtn) closeBtn.addEventListener("click", closeDrawer);

    if (searchInput) {
      searchInput.addEventListener("input", onMobileInput);
    }

    resultsContainer.addEventListener("click", (event) => {
      const termLink = event.target.closest(
        "[data-mobile-popular-search-term]",
      );
      if (termLink) {
        event.preventDefault();
        const term = termLink.getAttribute("data-mobile-popular-search-term");
        searchInput.value = term;
        searchInput.focus();
        hideMobileEmptyState();
        fetchMobileResults(term);
      }
    });

    drawer.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeDrawer();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      init();
    });
  } else {
    init();
  }
})();
 